import styles from "./group.module.scss";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppContext, AppAction } from "helpers/context";
import {
	Box, Button, IconButton, Table, TableBody, TableCell,
	TableHead, TableRow, Toolbar, TableSortLabel, Tooltip, TablePagination
} from "@mui/material";

import SearchInput from "./search-input/search-input";
import AddModal from "./add-modal/add-modal";
import DeleteModal from "./delete-modal/delete-modal";
import { getApiGroup, getEndPointList } from "api/layer";

const Group = () => {
	const [context, dispatch] = useContext(AppContext);
	const [t] = useTranslation();
	const [showFilters, setShowFilters] = useState(false);
	const [open, setOpen] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [currentInfo, setCurrentInfo] = useState<any>();
	const [searchData, setSearchData] = useState<{
		keyWord: string;
		startTime: any;
		endTime: any;
	}>({
		keyWord: "",
		startTime: null,
		endTime: null,
	});
	const [reverseSelect, setReverseSelect] = useState(false);
	const [page, setPage] = useState(0);
	const [tableData, setTableData] = useState([]);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [hoveredRow, setHoveredRow] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [sortByField, setSortByField] = useState<"group_name" | "endpoint_names" | "remark">("group_name");
	const [sortOrder, setSortOrder] = useState<{
		group_name: "asc" | "desc",
		endpoint_names: "asc" | "desc",
		remark: "asc" | "desc",
	}>({
		group_name: "asc",
		endpoint_names: "asc",
		remark: "asc",
	});
	const [endPointList, setEndPointList] = useState<any[]>([])

	const toggleFilters = () => {
		setShowFilters((prev) => !prev);
	};

	const clearFilters = () => {
		setShowFilters(false);
	};

	const handleSearch = (params: any, is: boolean) => {
		setSearchData((prevData) => ({ ...prevData, [params.name]: params.value }));
		if ((params.name === "startTime" || params.name === "endTime") && params.value && params.value.format("YYYY-MM-DD") === "Invalid Date") {
			return;
		}
		setReverseSelect(is);
		setPage(0);
	};

	const handleDelete = (item: any) => {
		setCurrentInfo(item);
		setOpenDeleteModal(true);
	};

	const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleSave = () => {
		setOpen(false);
		initData();
	};

	const initData = async () => {
		setIsLoading(true);
		let pageParam = {
			pageNo: page + 1,
			pageSize: rowsPerPage,
			reverseSelect: reverseSelect,
			sortOrderAsc: sortOrder[sortByField] === "asc",
			sortByField: sortByField,
			...searchData
		};

		let params: any = { ...searchData };


		getApiGroup(params, pageParam).then((res: any) => {
			if (res.code !== 200 || !res.data?.records) {
				return;
			}
			setTableData(res.data.records);
			setTotalCount(res.data.total);
			setIsLoading(false);
		});
	};

	const getEndPointSelect = () => {
		getEndPointList().then((res: any) => {
			if (res.code !== 200 || !res.data) {
				return;
			}
			setEndPointList(res.data)
		})
	}

	useEffect(() => {
		initData();
	}, [page, rowsPerPage, searchData, reverseSelect, sortByField, sortOrder]);

	useEffect(() => {
		getEndPointSelect()
	}, [])

	return (<div className={styles.container}>
		<div className={styles.title}>{t("userSystem.endPointGroup")}</div>
		<Toolbar style={{ justifyContent: "space-between", borderBottom: "1px solid #ccc", marginBottom: "5px", padding: "0" }}>
			<Button
				variant="contained"
				color="success"
				size="small"
				endIcon={<span className="material-icons">add</span>}
				onClick={() => {
					setOpen(true);
					setCurrentInfo(null);
				}}
			>
				{t("userSystem.addEndPointGroup")}
			</Button>

			<IconButton
				color="success"
				onClick={toggleFilters}
				style={{
					color: showFilters ? "#2e7d32" : "rgba(0, 0, 0, 0.54)",
					background: showFilters ? "#c1e5ca" : "",
				}}
			>
				<span className="material-icons">filter_list</span>
			</IconButton>
		</Toolbar>

		{showFilters && (
			<Box style={{ display: "flex", alignItems: "center", padding: "8px", backgroundColor: "#f5f5f5", justifyContent: "space-between" }}>
				<Box style={{ display: "flex", flex: 1, alignItems: "center" }}>
					<SearchInput onSearch={handleSearch} />

				</Box>
				<IconButton
					onClick={clearFilters}
					aria-label="clear"
					color="default"
				>
					<span className="material-icons">clear</span>
				</IconButton>
			</Box>
		)}

		<div style={{ flex: 1, overflow: "auto" }}>
			<Table
				stickyHeader
				aria-label="sticky table"
				size="small"
				style={{
					tableLayout: "fixed",
					width: "100%",
					minWidth: "100%"
				}}
			>
				<TableHead>
					<TableRow>
						<TableCell>
							<TableSortLabel
								active={sortByField === "group_name"}
								onClick={() => {
									setSortByField("group_name")
									let sort: any = {
										...sortOrder,
										group_name: sortOrder.group_name === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.group_name}
							>
								{t("userSystem.groupName")}
							</TableSortLabel>
						</TableCell>
						<TableCell>
							<TableSortLabel
								active={sortByField === "endpoint_names"}
								onClick={() => {
									setSortByField("endpoint_names")
									let sort: any = {
										...sortOrder,
										endpoint_names: sortOrder.endpoint_names === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.endpoint_names}
							>
								{t("userSystem.endPoint")}
							</TableSortLabel>
						</TableCell>
						<TableCell>
							<TableSortLabel
								active={sortByField === "remark"}
								onClick={() => {
									setSortByField("remark")
									let sort: any = {
										...sortOrder,
										remark: sortOrder.remark === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.remark}
							>
								{t("userSystem.description")}
							</TableSortLabel>
						</TableCell>
						<TableCell style={{ padding: 0, width: 0 }}></TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{isLoading ? (
						<TableRow>
							<TableCell colSpan={3}>
								<div className={styles.loading}>
									<div className="loading loading-spinner loading-lg"></div>
									<div>{t("layers.loading")}</div>
								</div>
							</TableCell>
						</TableRow>
					) : (
						tableData.map((item: any) => (
							<TableRow
								key={item.id}
								hover
								onMouseEnter={() => setHoveredRow(item.id)}
								onMouseLeave={() => setHoveredRow(null)}
								className={styles.row}
							>
								<TableCell>{item.groupName}</TableCell>
								<TableCell style={{ wordBreak: "break-all" }}>{item.endpointNames}</TableCell>
								<TableCell>{item.remark}</TableCell>
								<TableCell style={{ padding: 0, position: "relative" }}>
									{hoveredRow === item.id && (
										<div
											className={`${styles.icons}`}
										>
											<Tooltip
												title={t("tooltip.edit")}
											>
												<IconButton
													onClick={() => {
														setOpen(true);
														setCurrentInfo(item);
													}}
												>
													<span className="material-icons">edit</span>
												</IconButton>
											</Tooltip>

											<Tooltip
												title={t("tooltip.delete")}
											>
												<IconButton onClick={() => handleDelete(item)}>
													<span className="material-icons">delete</span>
												</IconButton>
											</Tooltip>
										</div>
									)}
								</TableCell>
							</TableRow>
						))
					)}
				</TableBody>
			</Table>
		</div>
		<div style={{ marginLeft: "auto" }}>
			<TablePagination
				component="div"
				count={totalCount}
				rowsPerPageOptions={[3, 10, 15, 25]}
				page={page}
				onPageChange={handleChangePage}
				rowsPerPage={rowsPerPage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				labelRowsPerPage={t("userSystem.rowsPerPage")}
				slotProps={{
					select: {
						native: true,
					},
				}}
			/>
		</div>

		{open && (
			<AddModal
				open={open}
				onClose={() => setOpen(false)}
				onSave={handleSave}
				data={currentInfo}
				endPointData={endPointList}
			/>
		)}

		{openDeleteModal && (
			<DeleteModal
				data={currentInfo}
				initData={initData}
				onClose={() => setOpenDeleteModal(false)}
			/>
		)}
	</div>)
};

export default Group;
